import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import {
    Instagram,
    Mail,
    MapPin,
    MessageCircle,
    MessageSquare,
    Phone,
    Send
} from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PageHero } from '@/components/generals/page-hero'
import { InquiryForm } from '@/components/inquiry/inquiry-form'
import { contactInfo } from '@/constants/site'

type Channel = {
    label: string
    value: string
    href: string
    icon: LucideIcon
}

const MESSENGERS: Channel[] = [
    { label: 'واتس‌اپ', value: contactInfo.whatsapp.display, href: contactInfo.whatsapp.url, icon: MessageCircle },
    { label: 'تلگرام', value: contactInfo.telegram.handle, href: contactInfo.telegram.url, icon: Send },
    { label: 'اینستاگرام', value: contactInfo.instagram.handle, href: contactInfo.instagram.url, icon: Instagram },
    { label: 'ایتا', value: contactInfo.eitaa.handle, href: contactInfo.eitaa.url, icon: MessageSquare }
]

const cardClasses = 'rounded-[1.75rem] border border-sand bg-card p-6 sm:p-7'

const ContactPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='تماس با ما'
                description='شماره‌های تماس، پیام‌رسان‌ها و فرم استعلام قیمت خوزکیمیا زاگرس برای خرید عمده و صادرات داروهای گیاهی.'
            />

            <PageHero
                eyebrow='تماس با ما'
                title='با ما در ارتباط باشید'
                description='برای خرید عمده، صادرات، دریافت نمونه یا برگه آنالیز محصولات، فرم استعلام را پر کنید یا مستقیم با ما تماس بگیرید.'
                crumbs={[
                    { label: 'خانه', href: '/' },
                    { label: 'تماس با ما' }
                ]}
            />

            <section id='contact' aria-label='راه‌های ارتباط' className='py-16 lg:py-24'>
                <div className='mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 lg:grid-cols-12 lg:gap-12'>
                    <div className='space-y-5 lg:col-span-5'>
                        <div className={cardClasses}>
                            <h2 className='flex items-center gap-2 text-2xl font-bold text-forest-900'>
                                <Phone aria-hidden='true' className='size-5 text-gold-deep' />
                                تماس مستقیم
                            </h2>

                            <ul className='mt-5 space-y-3'>
                                {contactInfo.phones.map(phone => (
                                    <li key={phone.tel}>
                                        <a
                                            href={`tel:${phone.tel}`}
                                            className='flex items-center justify-between gap-4 rounded-2xl border border-sand px-4 py-3.5 transition-colors hover:border-gold hover:bg-gold/5'
                                        >
                                            <span className='text-sm text-muted-foreground'>{phone.name}</span>
                                            <span dir='ltr' className='font-heading text-xl font-bold text-forest-900'>{phone.display}</span>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className={cardClasses}>
                            <h2 className='flex items-center gap-2 text-2xl font-bold text-forest-900'>
                                <MessageCircle aria-hidden='true' className='size-5 text-gold-deep' />
                                پیام‌رسان‌ها
                            </h2>

                            <ul className='mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2'>
                                {MESSENGERS.map(({ label, value, href, icon: Icon }) => (
                                    <li key={label}>
                                        <a
                                            href={href}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            className='flex items-center gap-3 rounded-2xl border border-sand px-4 py-3 transition-colors hover:border-gold hover:bg-gold/5'
                                        >
                                            <span className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-forest-700 text-gold'>
                                                <Icon aria-hidden='true' className='size-5' />
                                            </span>

                                            <span className='flex min-w-0 flex-col'>
                                                <span className='text-sm font-semibold text-forest-900'>{label}</span>
                                                <span dir='ltr' className='max-w-full self-start truncate text-xs text-muted-foreground'>{value}</span>
                                            </span>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className={cardClasses}>
                            <ul className='space-y-5'>
                                <li className='flex items-start gap-3'>
                                    <span className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-gold/15 text-gold-deep'>
                                        <Mail aria-hidden='true' className='size-5' />
                                    </span>

                                    <span className='flex min-w-0 flex-col'>
                                        <span className='text-sm font-semibold text-forest-900'>ایمیل</span>
                                        <a
                                            href={`mailto:${contactInfo.email}`}
                                            dir='ltr'
                                            className='self-start break-all text-sm text-forest-700 underline-offset-4 hover:underline'
                                        >
                                            {contactInfo.email}
                                        </a>
                                    </span>
                                </li>

                                <li className='flex items-start gap-3'>
                                    <span className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-gold/15 text-gold-deep'>
                                        <MapPin aria-hidden='true' className='size-5' />
                                    </span>

                                    <span className='flex flex-col'>
                                        <span className='text-sm font-semibold text-forest-900'>دفتر ایران</span>
                                        <span className='text-sm text-muted-foreground'>{contactInfo.address}</span>
                                    </span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className='lg:col-span-7'>
                        <InquiryForm />
                    </div>
                </div>
            </section>
        </Layout>
    )
}

export default ContactPage
